import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  Entitlement,
  Entitlements,
  Environment,
  Organization,
  ResponseState,
  Tenant,
  UserData
} from '@models';
import { EntitlementsService, OrgClientService, UserDetailsService } from '@services';
import { BehaviorSubject, forkJoin, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { LoggerService } from '../logger/logger.service';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private entitlementsService: EntitlementsService = inject(EntitlementsService);
  private userDetailsService: UserDetailsService = inject(UserDetailsService);
  private orgClientService: OrgClientService = inject(OrgClientService);
  private logger = inject(LoggerService).setContext('UserDataService');
  private username: string | null = null;
  private userOrgIds: string[] | null = null;
  private parentClientNames: string[] | null = null;
  private readonly requiredEntitlementName = 'CRG Sponsor Portal';
  private userDataLoaded$ = new BehaviorSubject<boolean>(false);
  isUserDataLoaded$: Observable<boolean> = this.userDataLoaded$.asObservable();

  constructor() {
    if (this.userDataExists()) {
      this.userDataLoaded$.next(true);
    }
  }

  getUserData(): Observable<UserData> {
    if (this.userDataLoaded$.value) {
      // Already loaded, return current data
      return of({
        username: this.getUsername(),
        userOrgIds: this.getUserOrgIds(),
        parentClientNames: this.getParentClientNames()
      } as UserData);
    }
    const entitlements$: Observable<ResponseState<Entitlements>> = this.entitlementsService
      .getUserEntitlements()
      .pipe(
        map((data) => ({ status: 'success', data }) as const),
        catchError((err: HttpErrorResponse) => {
          this.logger.warn('Error getting user entitlements:', err);
          if (err.status == 401) {
            return of({ status: 'error', errorCode: err.status } as const);
          }

          return of({ status: 'success', data: this.getEmptyEntitlements() } as const);
        })
      );

    const userDetails$: Observable<ResponseState<any>> = this.userDetailsService
      .getUserDetails()
      .pipe(
        map((data) => ({ status: 'success', data }) as const),
        catchError((err: HttpErrorResponse) => {
          this.logger.warn('Error getting user details:', err);
          if (err.status == 401) {
            return of({ status: 'error', errorCode: err.status } as const);
          }
          return of({ status: 'success', data: {} } as const);
        })
      );
    return forkJoin({
      entitlementsResponse: entitlements$,
      userDetailsResponse: userDetails$
    }).pipe(
      switchMap(({ entitlementsResponse, userDetailsResponse }) => {
        if (entitlementsResponse.status == 'error' || userDetailsResponse.status == 'error') {
          sessionStorage.setItem('alreadyRedirected', 'false');
          throw new Error('UserNotLoggedIn');
        }

        // Check if user has "CRG Sponsor Portal" entitlement in any organization
        const hasPortalAccess = this.checkPortalAccess(
          entitlementsResponse.data.organizations || []
        );

        if (!hasPortalAccess) {
          this.logger.error(`User does not have ${this.requiredEntitlementName} entitlement`);
          throw new Error('UserNotAuthorizedForPortal');
        }

        // Get all organization IDs that the user has access to
        const userOrganizations = entitlementsResponse.data.organizations.map((organization) =>
          organization.id.toString()
        );

        this.setUserOrgIds(userOrganizations);
        this.setUsername(userDetailsResponse.data.username);
        this.setPrincipalId(userDetailsResponse.data.principalId);
        //set whatfix variables
        this.setWfxUuid(userDetailsResponse.data.principalId);
        this.setWfxLegacyUuDiVar(userDetailsResponse.data.principalId);
        this.setWfxLocation();

        if (!this.username || !this.userOrgIds) {
          this.logger.error('Username or User Org IDs were not able to be fetched.');
          throw new Error('UsernameOrUserOrgIdsMissing');
        }

        return this.orgClientService.getParentClientNames(this.username, this.userOrgIds).pipe(
          map((clientResponse) => {
            this.setParentClientNames(clientResponse.names);
            //set whatfix variable
            this.setWfxOrg(clientResponse.names);
            this.setUserDataAsLoaded();
            this.userDataLoaded$.next(true);

            return {
              username: this.getUsername(),
              userOrgIds: this.getUserOrgIds(),
              parentClientNames: this.getParentClientNames()
            } as UserData;
          }),
          catchError((err) => {
            this.logger.error('Error fetching client names:', err);
            throw new Error('ClientNamesFetchError');
          })
        );
      }),
      catchError((err) => {
        this.logger.error('Error fetching user data:', err);
        throw err;
      })
    );
  }

  getUsername(): string | null {
    return this.username || sessionStorage.getItem('username');
  }

  getUserOrgIds(): string[] | null {
    return this.userOrgIds || JSON.parse(sessionStorage.getItem('userOrgIds') || 'null');
  }

  getPrincipalId(): string | null {
    return sessionStorage.getItem('principalId');
  }

  getParentClientNames(): string[] | null {
    return (
      this.parentClientNames || JSON.parse(sessionStorage.getItem('parentClientNames') || 'null')
    );
  }

  isUserDataAlreadyLoaded(): boolean {
    return sessionStorage.getItem('userDataAlreadyLoaded') === 'true';
  }

  clearUserData(): void {
    sessionStorage.clear();
    this.username = null;
    this.userOrgIds = null;
    this.parentClientNames = null;
    this.userDataLoaded$.next(false);
  }

  userDataExists(): boolean {
    /**
     * All user data should exists and different from null
     */
    const username = this.getUsername();
    if (!username) {
      return false;
    }

    const orgIds = this.getUserOrgIds();
    const parentClientNames = this.getParentClientNames();

    return !!(
      Array.isArray(orgIds) &&
      orgIds.length > 0 &&
      Array.isArray(parentClientNames) &&
      parentClientNames.length > 0
    );
  }

  private setUsername(username: string | null): void {
    this.username = username;
    if (this.username) {
      sessionStorage.setItem('username', this.username);
    } else {
      sessionStorage.removeItem('username');
    }
  }

  private setUserOrgIds(orgIds: string[] | null): void {
    this.userOrgIds = orgIds;
    if (this.userOrgIds) {
      sessionStorage.setItem('userOrgIds', JSON.stringify(this.userOrgIds));
    } else {
      sessionStorage.removeItem('userOrgIds');
    }
  }

  private setPrincipalId(principalId: string): void {
    sessionStorage.setItem('principalId', principalId);
  }

  private setParentClientNames(parentClientNames: string[]): void {
    this.parentClientNames = parentClientNames;
    sessionStorage.setItem('parentClientNames', JSON.stringify(this.parentClientNames));
  }

  private setUserDataAsLoaded(): void {
    sessionStorage.setItem('userDataAlreadyLoaded', 'true');
  }

  private checkPortalAccess(organizations: Organization[]): boolean {
    return organizations.some((org) =>
      org.environments.some((env: Environment) =>
        env.tenants.some((tenant: Tenant) =>
          tenant.entitlements.some(
            (entitlement: Entitlement) => entitlement.name === this.requiredEntitlementName
          )
        )
      )
    );
  }

  private getEmptyEntitlements(): Entitlements {
    return {
      messageName: '',
      organizations: []
    };
  }

  private setWfxUuid(principalId: string): void {
    sessionStorage.setItem('wfx_uuid', btoa(principalId));
  }

  private setWfxLegacyUuDiVar(principalId: string): void {
    sessionStorage.setItem('wfx_uudi', btoa(principalId));
  }

  private setWfxOrg(standardClientNames: string[]): void {
    const csvValue = standardClientNames
      .map((name) => {
        return `"${name.replace(/"/g, '""')}"`;
      })
      .join(',');
    sessionStorage.setItem('wfx_org', csvValue);
  }

  private setWfxLocation(): void {
    const locationTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    this.awsCheckip()
      .then((responseIp) => {
        const maskedIp = this.maskIp(responseIp);
        sessionStorage.setItem('wfx_location', `${locationTimeZone},${maskedIp}`);
      })
      .catch((error) => {
        sessionStorage.setItem('wfx_location', `${locationTimeZone}`);
        this.logger.warn('Failed to get response from AWS checkip service', error);
      });
  }

  private awsCheckip(): Promise<string> {
    return fetch('https://checkip.amazonaws.com')
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            `AWS Check IP request failed with status ${response.status} ${response.statusText}`
          );
        }
        return response.text();
      })
      .then((ip) => ip.trim());
  }

  private maskIp(ip: string): string {
    if (ip.includes('.')) {
      ip = this.cleanIpLastOctet(ip);
    } else if (ip.includes(':')) {
      ip = this.cleanIpLastHextets(ip);
    }
    return ip;
  }

  private cleanIpLastOctet(ip: string): string {
    const parts = ip.split('.');
    ip = '';
    if (parts.length === 4) {
      parts[3] = '0';
      ip = parts.join('.');
    }
    return ip;
  }

  private cleanIpLastHextets(ip: string): string {
    const parts = ip.split(':');
    ip = '';
    if (parts.length >= 4) {
      ip = parts.slice(0, 4).join(':') + ':0:0:0:0';
    }
    return ip;
  }
}
